import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { CalendarRange, Download, FolderArchive, Info } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Separator } from "@/shared/components/ui/separator";
import { cn } from "@/shared/lib/utils";
import {
  getApiErrorMessage,
  resolveBlobApiError,
} from "@/shared/lib/api-error";
import { downloadBlob } from "@/shared/lib/download";
import { fiscalExportService } from "@/features/fiscal-export/services/fiscal-export.service";
import { DocumentTypeSelector } from "@/features/fiscal-export/components/DocumentTypeSelector";
import { FiscalExportResult } from "@/features/fiscal-export/components/FiscalExportResult";
import {
  MAX_PERIOD_DAYS,
  PERIOD_PRESETS,
  countDaysInRange,
} from "@/features/fiscal-export/constants";
import {
  FISCAL_DOCUMENT_TYPE,
  type FiscalDocumentType,
  type FiscalExportResult as ExportResult,
} from "@/features/fiscal-export/types/fiscal-export.types";

const ALL_TYPES: FiscalDocumentType[] = [
  FISCAL_DOCUMENT_TYPE.NFSE,
  FISCAL_DOCUMENT_TYPE.NFCE,
];

export function ExportacaoFiscalPage() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [types, setTypes] = useState<FiscalDocumentType[]>(ALL_TYPES);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ExportResult | null>(null);
  const [resultPeriod, setResultPeriod] = useState({ start: "", end: "" });

  const validation = useMemo(() => {
    if (!startDate || !endDate) {
      return { valid: false, message: "Escolha a data inicial e a final." };
    }
    if (startDate > endDate) {
      return {
        valid: false,
        message: "A data inicial não pode ser depois da data final.",
      };
    }
    if (countDaysInRange(startDate, endDate) > MAX_PERIOD_DAYS) {
      return {
        valid: false,
        message: `O período não pode passar de ${MAX_PERIOD_DAYS} dias. Divida em partes menores.`,
      };
    }
    if (types.length === 0) {
      return {
        valid: false,
        message: "Escolha pelo menos um tipo de documento.",
      };
    }
    return { valid: true, message: null };
  }, [startDate, endDate, types]);

  const activePresetId = useMemo(() => {
    if (!startDate || !endDate) return null;
    return (
      PERIOD_PRESETS.find((preset) => {
        const range = preset.resolve();
        return range.startDate === startDate && range.endDate === endDate;
      })?.id ?? null
    );
  }, [startDate, endDate]);

  const handleSelectPreset = useCallback((presetId: string) => {
    const preset = PERIOD_PRESETS.find((item) => item.id === presetId);
    if (!preset) return;
    const range = preset.resolve();
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    setResult(null);
  }, []);

  const handleToggleType = useCallback((type: FiscalDocumentType) => {
    setTypes((current) =>
      current.includes(type)
        ? current.filter((item) => item !== type)
        : [...current, type],
    );
    setResult(null);
  }, []);

  const handleExport = async () => {
    if (!validation.valid) {
      if (validation.message) toast.error(validation.message);
      return;
    }

    setLoading(true);
    setResult(null);
    try {
      const exported = await fiscalExportService.downloadPeriodZip({
        startDate,
        endDate,
        types,
      });

      downloadBlob(exported.blob, exported.filename);
      setResult(exported);
      setResultPeriod({ start: startDate, end: endDate });
      toast.success("Arquivo gerado. O download começou.");
    } catch (error: unknown) {
      toast.error(
        getApiErrorMessage(
          await resolveBlobApiError(error),
          "Não foi possível gerar o arquivo.",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadAgain = useCallback(() => {
    if (!result) return;
    downloadBlob(result.blob, result.filename);
  }, [result]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">
          Exportação Fiscal
        </h1>
        <p className="text-muted-foreground">
          Baixe num único arquivo os XMLs das notas emitidas no período, para
          enviar à contabilidade.
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CalendarRange className="h-5 w-5 text-primary" />
            <CardTitle>Período</CardTitle>
          </div>
          <CardDescription>
            O filtro usa a data de emissão da nota. Uma nota emitida num mês e
            cancelada no mês seguinte sai no arquivo do mês em que foi emitida,
            junto com o XML do cancelamento.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="flex flex-wrap gap-2">
            {PERIOD_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                disabled={loading}
                onClick={() => handleSelectPreset(preset.id)}
                className={cn(
                  "rounded-full border px-3 py-1.5 text-sm transition-colors",
                  "disabled:opacity-60 disabled:cursor-not-allowed",
                  activePresetId === preset.id
                    ? "border-primary bg-primary/10 text-primary font-medium"
                    : "border-border text-muted-foreground hover:text-foreground hover:border-muted-foreground/40",
                )}
              >
                {preset.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="fiscal-export-start">Data inicial</Label>
              <Input
                id="fiscal-export-start"
                type="date"
                value={startDate}
                disabled={loading}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setResult(null);
                }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fiscal-export-end">Data final</Label>
              <Input
                id="fiscal-export-end"
                type="date"
                value={endDate}
                disabled={loading}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setResult(null);
                }}
              />
            </div>
          </div>

          <Separator />

          <div className="space-y-3">
            <div>
              <Label>Documentos</Label>
              <p className="text-sm text-muted-foreground">
                Cada tipo vai numa pasta própria dentro do arquivo.
              </p>
            </div>
            <DocumentTypeSelector
              selected={types}
              disabled={loading}
              onToggle={handleToggleType}
            />
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              {validation.message ??
                "Tudo certo. O arquivo será baixado pelo navegador."}
            </p>
            <Button
              onClick={handleExport}
              disabled={!validation.valid || loading}
              className="gap-2 self-start sm:self-auto"
            >
              <Download className="h-4 w-4" />
              {loading ? "Gerando arquivo..." : "Baixar arquivo"}
            </Button>
          </div>

          {result && (
            <FiscalExportResult
              result={result}
              startDate={resultPeriod.start}
              endDate={resultPeriod.end}
              onDownloadAgain={handleDownloadAgain}
            />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <FolderArchive className="h-5 w-5 text-primary" />
            <CardTitle>O que vem no arquivo</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <pre className="overflow-x-auto rounded-md border bg-muted/40 p-4 font-mono text-xs leading-relaxed text-muted-foreground">
            {`notas-fiscais_2026-08-01_a_2026-08-31.zip
├ nfse/
│  ├ 123_R456-abc_2026-08-03.xml
│  └ 124_R457-def_2026-08-05-cancelada.xml
└ nfce/
   ├ 42_C42-3f9a_2026-08-04.xml
   └ 43_C43-7b2e_2026-08-09-cancelada.xml`}
          </pre>

          <div className="flex items-start gap-3 text-sm text-muted-foreground">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="space-y-1">
              <p>
                São apenas os arquivos XML — o cupom e o PDF continuam
                disponíveis nota a nota, nas telas de Notas Fiscais e Notas
                Comerciais.
              </p>
              <p>
                Nota cancelada entra com o XML do cancelamento, marcado com
                <span className="font-mono"> -cancelada</span> no nome.
              </p>
              <p>
                Um tipo sem nenhuma nota no período não é erro: o arquivo sai só
                com o outro, e o resumo mostra o que entrou.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
